<script setup lang="ts">
import { computed, mergeProps, ref, useTemplateRef, watch, watchEffect } from 'vue'
import router from '@/router'
import { useRoute } from 'vue-router'
import { getCurrentYear, isRedundantNavigation } from '@/helpers'
import { eventBus } from '@/plugins/eventBus'
import { storeToRefs } from 'pinia'
import { useMenyStore } from '@/modules/meny/store'
import { VigoMenuItem } from '@/modules/meny/models/types'
import MenuButton from '@/modules/meny/components/MenuButton.vue'
import Snackbar from '@/modules/core/components/Snackbar.vue'
import { useCoreStore } from '@/modules/core/store'
import SpraakValg from '@/modules/core/components/SpraakValg.vue'
import { useAuthStore } from './modules/auth/store'
import { useNotatStore } from '@/modules/notater/store'
import HurtigvalgListe from '@/modules/ungdommer/components/HurtigvalgListe.vue'
import { useI18n } from 'vue-i18n'
import SkolearHurtigfilter from '@/modules/core/components/SkolearHurtigfilter.vue'
import { useElementBounding } from '@vueuse/core'

const { initateLogout } = useAuthStore()
const { idToken } = storeToRefs(useAuthStore())
const { updateFromIdToken } = useCoreStore()
const { t } = useI18n()

const { harFylkesAdminRettighet, viewportHeightWithoutHeaderAndFooter } =
  storeToRefs(useCoreStore())

watch(idToken, (newToken, _oldToken) => updateFromIdToken(newToken), { immediate: true })

const menuOpen = ref<boolean>(false)
const route = useRoute()

const { hentMenyer } = useMenyStore()
const { hentAktivBrukerMedRettigheter } = useCoreStore()
const { hentUleseNotater } = useNotatStore()

const { lasterMenyer, menus, profilHeaderMenu } = storeToRefs(useMenyStore())

const { applicationActive } = storeToRefs(useAuthStore())

const snackbarColor = ref('error')
const snackbarText = ref('')
const showSnackbar = ref(false)

let ulesteNotaterTimer: ReturnType<typeof setInterval> | undefined = undefined

hentMenyer()
hentAktivBrukerMedRettigheter()

watch(
  applicationActive,
  function (newActive, _oldActive) {
    if (newActive && !ulesteNotaterTimer) {
      hentUleseNotater()
      ulesteNotaterTimer = setInterval(() => {
        hentUleseNotater()
      }, 60000)
    } else if (ulesteNotaterTimer) {
      clearInterval(ulesteNotaterTimer)
      ulesteNotaterTimer = undefined
    }
  },
  { immediate: true }
)

const harRettigheterTilBrukeradmin = computed(() => {
  if (Array.isArray(idToken?.value?.permissions)) {
    const permissions = Array.from(idToken?.value?.permissions)
    const brukeradminRettighet = permissions?.find((perm: string) => 'vigo:ot:brukeradmin' === perm)
    return !!brukeradminRettighet
  }
  return false
})

const annetMenuItems = computed((): VigoMenuItem[] => {
  const sentraleOppgaverMeny = {
    text: 'Sentrale oppgaver',
    icon: 'mdi-list-status',
    route: '/sentraleOppgaver',
    external: false,
    disabled: false
  }
  const brukereMeny = {
    text: 'Brukere',
    icon: 'mdi-account-group',
    route: '/brukere',
    external: false
  }
  const kontaktpersonerMeny = {
    text: 'Kontaktpersoner',
    icon: 'mdi-contacts',
    route: '/kontaktpersoner',
    external: false
  }
  const vigoSkole = {
    text: 'Vigo Skole',
    icon: 'mdi-open-in-new',
    route: 'https://www.vigo.no/vigor/servlet/main?cmd=Login',
    external: true
  }
  const annetMenuItemsMedRettigheter: VigoMenuItem[] = []
  if (harFylkesAdminRettighet.value) {
    annetMenuItemsMedRettigheter.push(sentraleOppgaverMeny)
  }
  if (harRettigheterTilBrukeradmin.value) {
    annetMenuItemsMedRettigheter.push(brukereMeny)
  }
  annetMenuItemsMedRettigheter.push(kontaktpersonerMeny)
  annetMenuItemsMedRettigheter.push(vigoSkole)
  return annetMenuItemsMedRettigheter
})

function navigateTo(menuItem: VigoMenuItem): void {
  if (menuItem.external) {
    window.open(menuItem.route, '_blank')
    return
  }
  if (!isRedundantNavigation(route.name, menuItem.route)) {
    router.push({ path: menuItem.route })
  }
}

try {
  eventBus.$on('showSuccess', (success: string) => showSuccess(success))
  eventBus.$on('showError', (error: string) => showError(error))
  eventBus.$on('showWarning', (error: string) => showWarning(error))
} catch (_e) {
  showError('Failed to initialise app')
}

function showError(error: string) {
  snackbarColor.value = 'error'
  openSnackbar(error)
}

function showSuccess(success: string) {
  snackbarColor.value = 'success'
  openSnackbar(success)
}

function showWarning(warning: string) {
  snackbarColor.value = 'warning'
  openSnackbar(warning)
}

function openSnackbar(text: string) {
  if (showSnackbar.value) {
    showSnackbar.value = false
  }
  showSnackbar.value = true
  snackbarText.value = text
}

const VERSION = import.meta.env.VITE_APP_VERSION

const viewport = useTemplateRef('viewport')
const header = ref()
const footer = useTemplateRef('footer')

const { height: viewportHeight } = useElementBounding(viewport)
const { height: footerHeight } = useElementBounding(footer)
const { height: headerHeight } = useElementBounding(header)

watchEffect(() => {
  if (viewportHeight.value && footerHeight.value && headerHeight.value) {
    viewportHeightWithoutHeaderAndFooter.value = Math.floor(
      viewportHeight.value - (footerHeight.value + headerHeight.value)
    )
  }
})
</script>

<template>
  <v-app>
    <div ref="viewport" class="viewport"></div>
    <v-app-bar ref="header" elevation="2" color="primary" aria-labelledby="navigasjon_label">
      <h1 id="navigasjon_label" class="sr-only text-h5 pl-2">VIGO OT</h1>
      <template #append>
        <v-app-bar-nav-icon v-if="!!$vuetify.display.mobile" @click.stop="menuOpen = !menuOpen" />
      </template>
      <v-item-group v-if="!$vuetify.display.mobile" selected-class="bg-primary">
        <v-container fluid>
          <v-row no-gutters justify="start" align="start" align-content="start">
            <v-col v-for="(menu, index) in menus" :key="index" align-self="start">
              <v-item>
                <menu-button :menu="menu" />
              </v-item>
            </v-col>
            <v-col>
              <v-item>
                <v-menu close-on-content-click>
                  <template #activator="{ props }">
                    <v-tooltip
                      :disabled="$vuetify.display.lgAndUp"
                      :text="t('meny.annet', 'Annet')"
                      location="bottom"
                    >
                      <template #activator="{ props: tooltipProps }">
                        <v-btn
                          variant="text"
                          v-bind="mergeProps(props, tooltipProps)"
                          :size="$vuetify.display.lgAndUp ? 'default' : 'small'"
                          class="compact-icons"
                          :aria-label="t('meny.annet', 'Annet')"
                        >
                          {{ $vuetify.display.lgAndUp ? t('meny.annet', 'Annet') : '' }}
                          <template v-if="!$vuetify.display.lgAndUp" #prepend>
                            <v-icon icon="mdi-folder-settings-outline" />
                          </template>
                          <template #append="">
                            <v-icon icon="mdi-menu-down" />
                          </template>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-list>
                    <v-list-item v-for="(item, i) in annetMenuItems" :key="i">
                      <v-btn
                        :size="$vuetify.display.lgAndUp ? 'default' : 'small'"
                        :disabled="item.disabled"
                        variant="text"
                        block
                        @click="navigateTo(item)"
                      >
                        <template #default>
                          <div style="width: 180px">{{ item.text }}</div>
                        </template>
                        <template #append>
                          <v-icon :icon="item.icon" />
                        </template>
                      </v-btn>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-item>
            </v-col>
          </v-row>
        </v-container>
      </v-item-group>
      <v-spacer />
      <skolear-hurtigfilter />
      <v-menu close-on-content-click>
        <template #activator="{ props }">
          <v-tooltip
            :disabled="$vuetify.display.lgAndUp"
            :text="t('meny.hurtigvalg', 'Hurtigvalg')"
            location="bottom"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                variant="text"
                v-bind="mergeProps(props, tooltipProps)"
                :size="$vuetify.display.lgAndUp ? 'default' : 'small'"
                class="compact-icons"
                :aria-label="t('meny.hurtigvalg', 'Hurtigvalg')"
              >
                {{ $vuetify.display.lgAndUp ? t('meny.hurtigvalg', 'Hurtigvalg') : '' }}
                <template v-if="!$vuetify.display.lgAndUp" #prepend>
                  <v-icon icon="mdi-star" />
                </template>
                <template #append="">
                  <v-icon icon="mdi-menu-down" />
                </template>
              </v-btn>
            </template>
          </v-tooltip>
        </template>
        <hurtigvalg-liste />
      </v-menu>
      <v-tooltip :disabled="$vuetify.display.lgAndUp" :text="'Profil'" location="bottom">
        <template #activator="{ props: tooltipProps }">
          <v-btn
            :icon="profilHeaderMenu.icon"
            :size="$vuetify.display.lgAndUp ? 'default' : 'small'"
            v-bind="tooltipProps"
            aria-label="Profil"
            @click="navigateTo(profilHeaderMenu)"
          />
        </template>
      </v-tooltip>
      {{ `${idToken?.firstname ?? ''} ${idToken?.lastname ?? ''}` }}
      <v-btn
        icon="mdi-logout"
        :size="$vuetify.display.lgAndUp ? 'default' : 'small'"
        title="Logg ut"
        aria-label="Logg ut"
        variant="text"
        @click="initateLogout()"
      />
    </v-app-bar>
    <v-navigation-drawer v-if="$vuetify.display.mobile" v-model="menuOpen" location="end" temporary>
      <v-list :lines="false" nav>
        <v-list-item
          v-for="(item, i) in menus"
          :key="i"
          :value="item"
          color="primary"
          @click="navigateTo(item)"
        >
          <template #prepend>
            <v-icon :icon="item.icon"></v-icon>
          </template>
          <v-list-item-title>{{ item.text }}</v-list-item-title>
        </v-list-item>
        <v-list-group value="Annet">
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-folder-outline"
              :title="t('meny.annet', 'Annet')"
            ></v-list-item>
          </template>
          <v-list-item
            v-for="(item, i) in annetMenuItems"
            :key="i"
            :prepend-icon="item.icon"
            :title="item.text"
            :value="item.text"
            @click="navigateTo(item)"
          ></v-list-item>
        </v-list-group>
      </v-list>
    </v-navigation-drawer>
    <v-main>
      <v-progress-linear v-if="lasterMenyer" color="yellow-darken-2" indeterminate />
      <router-view ref="routerRef" />
    </v-main>
    <v-footer ref="footer" class="bg-primary mx-0 px-0" style="max-height: fit-content">
      <Snackbar
        :color="snackbarColor"
        :text="snackbarText"
        :open="showSnackbar"
        @update:open="showSnackbar = false"
      />
      <v-container fluid>
        <v-row justify="center" align-content="center" align="center">
          <SpraakValg />
        </v-row>
        <v-row class="ma-0" justify="center" align-content="center" align="center">
          <v-col cols="12" align-self="center">
            <div class="text-center">
              {{ `©2018-${getCurrentYear()} IST Norge AS/Novari IKS` }}
            </div>
            <div class="text-center">Versjon: {{ VERSION }}</div>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<style scoped>
.viewport {
  position: absolute;
  height: 100svh;
  height: 100vh;
}

.compact-icons :deep(.v-btn__prepend) {
  margin-inline-end: 0px !important;
}

.compact-icons :deep(.v-btn__append) {
  margin-inline-start: 0px !important;
}

.compact-icons :deep(.v-btn__content) {
  gap: 2px !important;
}
</style>
